import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'

type DraftSummary = {
  release_id: number
  version: string
  total_changes: number
  total_draft: number
  total_awaiting: number
  total_approved: number
  total_rejected: number
  my_ready: number
  my_awaiting: number
}

type RejectedChange = {
  change_id: number
  release_id: number
  release_version: string
  dataset: string
  operacao: 'INSERT' | 'UPDATE' | 'DELETE'
  record_key: string
  dados_novos: Record<string, unknown> | null
  rejection_reason: string
  reviewed_at: string | null
  reviewed_by: string
}

const DATASET_LABELS: Record<string, string> = {
  empreendimentos: 'Empreendimentos',
  suite_help: 'Suite Help',
  qualificacoes: 'Qualificações',
  acionamentos: 'Acionamentos',
  servicos: 'Tabela de Serviços',
  irregularidades: 'Irregularidades',
}

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function formatDate(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR')
}

export function DraftSafetyBar() {
  const { profile } = useAuth()
  const [draft, setDraft] = useState<DraftSummary | null>(null)
  const [rejected, setRejected] = useState<RejectedChange[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState('')

  async function loadDraft(options?: { silent?: boolean }) {
    if (!options?.silent) setLoading(true)
    setLoadError('')
    try {
      const summaryResult = await supabase.rpc('get_my_draft_summary')
      if (summaryResult.error) throw summaryResult.error
      const row = Array.isArray(summaryResult.data) ? summaryResult.data[0] : summaryResult.data
      setDraft((row as DraftSummary | undefined) ?? null)

      if (profile?.role === 'gestao') {
        const rejectedResult = await supabase.rpc('get_my_rejected_changes')
        if (rejectedResult.error) throw rejectedResult.error
        setRejected((rejectedResult.data ?? []) as RejectedChange[])
      } else {
        setRejected([])
      }
    } catch (error) {
      setDraft(null)
      setRejected([])
      setLoadError(errorMessage(error, 'Não foi possível carregar o estado do rascunho.'))
    } finally {
      if (!options?.silent) setLoading(false)
    }
  }

  useEffect(() => {
    void loadDraft()

    // A barra é um componente independente dos editores. Sincroniza em curto
    // intervalo para refletir INSERT/UPDATE/DELETE recém-preparados sem F5.
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !saving) {
        void loadDraft({ silent: true })
      }
    }, 1200)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void loadDraft({ silent: true })
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [profile?.id, profile?.role, saving])

  async function submitForApproval() {
    if (!draft || profile?.role !== 'gestao') return
    setSaving(true)
    try {
      const result = await supabase.rpc('submit_my_draft_changes', {
        target_release_id: draft.release_id,
      })
      if (result.error) throw result.error
      window.alert(`${result.data ?? draft.my_ready} alteração(ões) enviada(s) para aprovação do Administrador.`)
      await loadDraft({ silent: true })
      window.location.reload()
    } catch (error) {
      window.alert(errorMessage(error, 'Não foi possível enviar para aprovação.'))
    } finally {
      setSaving(false)
    }
  }

  async function discardRejected(item: RejectedChange) {
    if (!window.confirm(`Descartar a alteração rejeitada de “${item.record_key}”? A base oficial não será modificada.`)) return
    setSaving(true)
    try {
      const result = await supabase
        .from('draft_changes')
        .delete()
        .eq('id', item.change_id)
        .select('id')
        .maybeSingle()
      if (result.error) throw result.error
      if (!result.data) throw new Error('A alteração rejeitada não foi removida do rascunho.')
      await loadDraft({ silent: true })
      window.location.reload()
    } catch (error) {
      window.alert(errorMessage(error, 'Não foi possível descartar a alteração rejeitada.'))
    } finally {
      setSaving(false)
    }
  }

  async function discardAll() {
    if (!draft) return
    if (profile?.role === 'gestao' && draft.my_awaiting > 0) {
      window.alert('Há alteração(ões) aguardando aprovação. Elas ficam bloqueadas até o Administrador aprovar ou rejeitar.')
      return
    }

    const confirmation = window.prompt(
      `Descartar TODAS as ${draft.total_changes} alterações do rascunho ${draft.version}, em todas as bases?\n\nNenhuma base oficial será alterada.\n\nDigite DESCARTAR ${draft.total_changes} para confirmar.`,
    )
    if (confirmation !== `DESCARTAR ${draft.total_changes}`) return

    setSaving(true)
    try {
      const result = await supabase
        .from('draft_changes')
        .delete()
        .eq('release_id', draft.release_id)

      if (result.error) throw result.error
      window.location.reload()
    } catch (error) {
      window.alert(errorMessage(error, 'Não foi possível descartar o rascunho.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return null

  if (loadError && profile?.role === 'gestao') {
    return (
      <div className="notice error" style={{ position: 'fixed', right: 22, bottom: 22, zIndex: 1200, maxWidth: 520 }}>
        Falha ao carregar aprovação: {loadError}
      </div>
    )
  }

  if (!draft) return null

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        right: 22,
        bottom: 22,
        zIndex: 1200,
        display: 'grid',
        gap: 10,
        width: 'min(820px, calc(100vw - 44px))',
        padding: '12px 14px',
        border: '1px solid rgba(0,174,202,.25)',
        borderRadius: 14,
        background: 'rgba(255,255,255,.98)',
        boxShadow: '0 16px 44px rgba(16,34,49,.18)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 2, minWidth: 220, flex: '1 1 260px' }}>
          <strong style={{ fontSize: '.78rem' }}>Rascunho {draft.version}</strong>
          <span style={{ color: '#647787', fontSize: '.68rem' }}>
            {draft.total_changes} alteração(ões) · {draft.total_awaiting} aguardando aprovação · {draft.total_approved} aprovada(s)
          </span>
          {draft.total_rejected > 0 && (
            <span style={{ color: '#bd3447', fontSize: '.66rem' }}>{draft.total_rejected} rejeitada(s) para correção</span>
          )}
        </div>

        {profile?.role === 'gestao' && draft.my_ready > 0 && (
          <button
            type="button"
            className="primary-button compact"
            disabled={saving}
            onClick={() => void submitForApproval()}
          >
            {saving ? 'Enviando...' : `Enviar para aprovação (${draft.my_ready})`}
          </button>
        )}

        {profile?.role === 'gestao' && draft.my_awaiting > 0 && draft.my_ready === 0 && (
          <span className="status-chip warning">Aguardando Administrador</span>
        )}

        <button
          type="button"
          className="danger-button compact"
          disabled={saving || (profile?.role === 'gestao' && draft.my_awaiting > 0)}
          onClick={() => void discardAll()}
        >
          {saving ? 'Processando...' : 'Descartar todo o rascunho'}
        </button>
      </div>

      {profile?.role === 'gestao' && rejected.length > 0 && (
        <div style={{ display: 'grid', gap: 7, maxHeight: 210, overflow: 'auto', paddingTop: 9, borderTop: '1px solid #edf2f5' }}>
          {rejected.map((item) => (
            <div
              key={item.change_id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                padding: '9px 10px',
                border: '1px solid rgba(189,52,71,.18)',
                borderRadius: 10,
                background: 'rgba(189,52,71,.045)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <strong style={{ display: 'block', color: '#bd3447', fontSize: '.7rem' }}>
                  Rejeitado · {DATASET_LABELS[item.dataset] ?? item.dataset} · {item.record_key}
                </strong>
                <span style={{ display: 'block', marginTop: 4, color: '#102231', fontSize: '.69rem' }}>
                  <b>Motivo:</b> {item.rejection_reason}
                </span>
                <span style={{ display: 'block', marginTop: 3, color: '#647787', fontSize: '.62rem' }}>
                  Revisado por {item.reviewed_by} · {formatDate(item.reviewed_at)}. Corrija o conteúdo no editor antes de reenviar. Se for um registro novo (INSERT), descarte e cadastre novamente com a correção.
                </span>
              </div>
              <button
                type="button"
                className="secondary-button compact"
                disabled={saving}
                onClick={() => void discardRejected(item)}
              >
                Descartar rejeitado
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
